/**
 * 운영 도구 - 지점 선택 페이지
 * 로그인 후 근무 지점을 선택
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getStores } from '../services/api';
import redirectAfterLogin from '../utils/redirectAfterLogin';
import './StoreSelectPage.css';

export default function StoreSelectPage() {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  // 딥링크 복원 — `/zoolungman` 을 열었다가 지점선택을 거쳐도 목적지를 잃지 않는다.
  // `ProtectedRoute` 가 `/store-select` 로 보낼 때 `state.from` 을 함께 넘긴다.
  const location = useLocation();

  useEffect(() => {
    // 계정에 매장이 귀속된 경우 (store_id가 JWT에 포함) 자동으로 건너뛰기
    if (user?.store_id) {
      localStorage.setItem('store_id', user.store_id);
      // store_name 은 빈값으로 덮어쓰지 않는다(login 응답엔 없을 수 있음 → StoreBadge 가려짐 방지).
      if (user.store_name) {
        localStorage.setItem('store_name', user.store_name);
      }
      navigate(redirectAfterLogin(location.state), { replace: true });
      return;
    }

    const fetchStores = async () => {
      try {
        const res = await getStores();
        setStores(res.stores || []);
      } catch (err) {
        setError(err.message || '지점 목록을 불러올 수 없습니다');
      } finally {
        setLoading(false);
      }
    };
    fetchStores();
    // `location.state` 를 읽으므로 의존성에 넣는다 — 빠지면 react-hooks/exhaustive-deps
    // 경고가 뜨고 CI 빌드(`CI=true`)에서는 경고가 오류로 승격된다.
  }, [user, navigate, location.state]);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const handleSelect = (store) => {
    localStorage.setItem('store_id', store.store_id);
    localStorage.setItem('store_name', store.store_name);
    navigate(redirectAfterLogin(location.state), { replace: true });
  };

  return (
    <div className="store-select-page">
      {/* 배경 장식 */}
      <div className="store-deco store-deco-1"></div>
      <div className="store-deco store-deco-2"></div>

      {/* 상단 로그아웃 */}
      <header className="store-top-bar">
        <span className="store-top-user">{user?.name || user?.username || '운영자'}</span>
        <button className="store-btn-logout" onClick={handleLogout}>로그아웃</button>
      </header>

      <div className="store-select-content">
        {/* 헤더 */}
        <div className="store-select-header">
          <span className="store-select-icon">🏢</span>
          <h1>지점 선택</h1>
          <p className="store-select-sub">근무 지점을 선택해주세요</p>
        </div>

        {/* 로딩 */}
        {loading && (
          <div className="store-loading">
            <span className="store-spinner" />
            <p>지점 목록 불러오는 중...</p>
          </div>
        )}

        {/* 에러 */}
        {error && (
          <div className="store-error">
            <span>⚠️</span>
            <p>{error}</p>
          </div>
        )}

        {/* 지점 카드 목록 */}
        {!loading && !error && (
          <div className="store-grid">
            {stores.map((store) => (
              <button
                key={store.store_id}
                className="store-card"
                onClick={() => handleSelect(store)}
              >
                <span className="store-card-icon">🏬</span>
                <h2 className="store-card-name">{store.store_name}</h2>
                {store.address && (
                  <p className="store-card-address">{store.address}</p>
                )}
              </button>
            ))}
          </div>
        )}

        {/* 빈 목록 */}
        {!loading && !error && stores.length === 0 && (
          <div className="store-empty">
            <p>등록된 지점이 없습니다</p>
          </div>
        )}
      </div>
    </div>
  );
}
